import { prefersReducedMotion } from '@theme/utilities';
import { blobPath, shapePath, SHAPES } from '@theme/blob-shapes';

const OPEN_EASE = 0.16; // how fast the open/close morph settles
const ENERGY_EASE = 0.08; // how fast hover "heats up" / cools down
const ENERGY_SPEED = 1.3; // hover advances the wobble clock up to (1 + this)x
const MAX_DT = 50; // clamp frame delta so a backgrounded tab can't jump the clock
const FRAME_MS = 1000 / 30; // throttle the SVG repaint — slow drift reads the same at 30fps
const EPSILON = 0.001;

/**
 * Decorative SVG blob. Closed rows drift continuously like a lava lamp; the open
 * row settles on its still active shape. Hovering a closed row energizes the
 * drift. Fully static under reduced motion, and paused while off-screen.
 */
class BlobMorph extends HTMLElement {
  /** @type {SVGPathElement | null} */ #path = null;
  /** @type {HTMLElement | null} */ #summary = null;
  /** @type {HTMLDetailsElement | null} */ #details = null;
  #shape = Object.keys(SHAPES)[0] ?? 'petal-square';
  #hover = false;
  #open = false;
  #openProgress = 0; // eased 0..1 (closed..open)
  #energy = 0; // eased 0..1 hover energy
  #clock = 0; // accumulated wobble time (ms)
  #lastTime = 0;
  #lastPaint = 0;
  /** @type {number | null} */ #raf = null;
  /** @type {IntersectionObserver | null} */ #observer = null;
  #controller = new AbortController();

  connectedCallback() {
    const path = this.querySelector('path');
    if (!(path instanceof SVGPathElement)) return;
    this.#path = path;

    if (this.dataset.shape && SHAPES[this.dataset.shape]) this.#shape = this.dataset.shape;

    const details = this.closest('details');
    this.#details = details instanceof HTMLDetailsElement ? details : null;
    this.#summary = this.closest('summary') ?? this.#details?.querySelector('summary') ?? null;
    this.#open = this.#details?.open ?? false;
    this.#openProgress = this.#open ? 1 : 0;

    // Paint a wobble-free shape immediately to avoid an empty-path flash.
    this.#path.setAttribute('d', shapePath(this.#shape, this.#openProgress));

    // Fresh controller per connect — the aborted one from a previous
    // disconnect (editor section reorder) would silently drop listeners.
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    if (prefersReducedMotion()) {
      this.#details?.addEventListener('toggle', this.#onToggleStatic, { signal });
      return;
    }

    this.#summary?.addEventListener('pointerenter', this.#onEnter, { signal });
    this.#summary?.addEventListener('pointerleave', this.#onLeave, { signal });
    this.#details?.addEventListener('toggle', this.#onToggle, { signal });

    // Only animate while the blob is on-screen.
    this.#observer = new IntersectionObserver(this.#onIntersect, { rootMargin: '120px' });
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#controller.abort();
    this.#observer?.disconnect();
    this.#observer = null;
    this.#stop();
  }

  /** @param {IntersectionObserverEntry[]} entries */
  #onIntersect = (entries) => {
    if (entries.some((entry) => entry.isIntersecting)) this.#start();
    else this.#stop();
  };

  #onEnter = () => {
    this.#hover = true;
    this.#start();
  };

  #onLeave = () => {
    this.#hover = false;
    this.#start();
  };

  #onToggle = () => {
    this.#open = this.#details?.open ?? false;
    this.#start();
  };

  #onToggleStatic = () => {
    this.#open = this.#details?.open ?? false;
    this.#openProgress = this.#open ? 1 : 0;
    this.#path?.setAttribute('d', shapePath(this.#shape, this.#openProgress));
  };

  #start() {
    if (this.#raf) return;
    this.#lastTime = 0;
    this.#lastPaint = 0;
    this.#raf = requestAnimationFrame(this.#tick);
  }

  #stop() {
    if (this.#raf) cancelAnimationFrame(this.#raf);
    this.#raf = null;
    this.#lastTime = 0;
    this.#lastPaint = 0;
  }

  /** @param {number} now */
  #tick = (now) => {
    const dt = this.#lastTime ? Math.min(now - this.#lastTime, MAX_DT) : 16;
    this.#lastTime = now;

    this.#openProgress += ((this.#open ? 1 : 0) - this.#openProgress) * OPEN_EASE;
    const energyTarget = this.#hover && !this.#open ? 1 : 0;
    this.#energy += (energyTarget - this.#energy) * ENERGY_EASE;
    this.#clock += dt * (1 + this.#energy * ENERGY_SPEED);

    // An open row settles on its still active shape; everything else keeps drifting.
    const settled = this.#open && this.#openProgress > 1 - EPSILON && this.#energy < EPSILON;
    if (settled) {
      this.#openProgress = 1;
      this.#energy = 0;
      this.#path?.setAttribute('d', blobPath(this.#shape, { open: 1 }));
      this.#stop();
      return;
    }

    // State advances every frame for accurate timing, but the costly path rebuild
    // + repaint is throttled — slow drift reads identically at ~30fps.
    if (now - this.#lastPaint >= FRAME_MS) {
      this.#lastPaint = now;
      this.#path?.setAttribute(
        'd',
        blobPath(this.#shape, { open: this.#openProgress, clock: this.#clock, energy: this.#energy })
      );
    }
    this.#raf = requestAnimationFrame(this.#tick);
  };
}

if (!customElements.get('blob-morph')) {
  customElements.define('blob-morph', BlobMorph);
}
